import { antdMessage } from '@hellodarwin/core/lib/features/providers/antd-app-provider';
import ConversationPanel from '@hellodarwin/app-features/lib/features/conversations/components/ConversationPanel';
import type {
  MentionCandidate,
  MentionExpansionOption,
} from '@hellodarwin/app-features/lib/features/conversations/types';
import { useTranslations } from '@hellodarwin/core/lib/features/providers/translations-provider';
import { useCallback, useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../app/app-hooks';
import type { ClientConversationContext } from '../../../../features/api/slices/client-conversation-slice';
import {
  deleteConversationMessage,
  fetchConversation,
  selectConversationHasMoreByContext,
  selectConversationLoadingByContext,
  selectConversationMessagesByContext,
  selectConversationPageByContext,
  selectConversationSnapshotAtByContext,
  sendConversationMessage,
} from '../../../../features/api/slices/client-conversation-slice';
import { useNewClientApi } from '../../../../features/api/use-client-api';
import { useUser } from '../../../../features/providers/user-provider';

interface ClientConversationBlockProps {
  programId: string;
  mentionCandidates?: MentionCandidate[];
}

const ClientConversationBlock = ({
  programId,
  mentionCandidates = [],
}: ClientConversationBlockProps) => {
  const api = useNewClientApi();
  const dispatch = useAppDispatch();
  const context: ClientConversationContext = useMemo(
    () => ({ type: 'program', id: programId }),
    [programId],
  );
  const { t } = useTranslations();

  const { contact } = useUser();
  const selfContactId = contact?.contact_id || undefined;
  const allMessages = useAppSelector((s) =>
    selectConversationMessagesByContext(s, context),
  );
  const hasMore = useAppSelector((s) =>
    selectConversationHasMoreByContext(s, context),
  );
  const page = useAppSelector((s) =>
    selectConversationPageByContext(s, context),
  );
  const snapshotAt = useAppSelector((s) =>
    selectConversationSnapshotAtByContext(s, context),
  );

  const limit = 20;
  const loadConversation = useCallback(() => {
    const initialSnapshot = new Date().toISOString();
    dispatch(
      fetchConversation({
        api,
        context,
        page: 1,
        snapshotAt: initialSnapshot,
        limit,
      }),
    );
  }, [dispatch, api, context, limit]);

  useEffect(() => {
    loadConversation();
  }, [loadConversation]);

  const onSend = useCallback(
    async (content: string, replyParentMessageId?: string) => {
      try {
        await dispatch(
          sendConversationMessage({
            api,
            context,
            content,
            replyParentMessageId,
          }),
        ).unwrap();
      } catch (e) {
        antdMessage.error(t('application|conversationSendFail'));
        console.error('Failed to send message', e);
      }
    },
    [dispatch, api, context, t],
  );

  const onDelete = useCallback(
    async (messageId: string) => {
      try {
        await dispatch(
          deleteConversationMessage({ api, context, messageId }),
        ).unwrap();
        antdMessage.success(t('application|conversationDeleteSuccess'));
      } catch (e) {
        console.error('Failed to delete message', e);
        antdMessage.error(t('application|conversationDeleteFail'));
      }
    },
    [dispatch, api, context, t],
  );

  const loadingMessages = useAppSelector((s) =>
    selectConversationLoadingByContext(s, context),
  );
  const mentionExpansionOptions: MentionExpansionOption[] = useMemo(
    () => [
      {
        label: t('application|conversationMentionAllHelloDarwinTeam'),
        candidates: mentionCandidates,
      },
    ],
    [mentionCandidates, t],
  );

  const canLoadMore = hasMore;
  const handleLoadMore = () => {
    if (!loadingMessages && hasMore) {
      dispatch(
        fetchConversation({
          api,
          context,
          page: (page || 0) + 1,
          snapshotAt,
          limit,
        }),
      );
    }
  };

  return (
    <ConversationPanel
      messages={allMessages}
      mentionCandidates={mentionCandidates}
      mentionExpansionOptions={mentionExpansionOptions}
      onSend={onSend}
      initialText=""
      hasMore={canLoadMore}
      loadingMore={loadingMessages}
      onLoadMore={handleLoadMore}
      selfRole="client"
      onDeleteMessage={onDelete}
      requireMention={false}
      selfId={selfContactId}
    />
  );
};

export default ClientConversationBlock;
